import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Capabilities', path: '/capabilities' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav 
      data-testid="navigation" 
      className="fixed top-0 left-0 right-0 z-50 bg-black border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-32 md:h-36 lg:h-40">
          {/* Logo */}
          <Link 
            to="/" 
            data-testid="logo-link"
            className="block hover:opacity-80 transition-opacity duration-300"
          >
            <img 
              src="/logo-gold-trimmed.jpeg" 
              alt="Novum Royale" 
              className="h-20 md:h-24 lg:h-28 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-12">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase()}`}
                className={`text-xs uppercase tracking-[0.2em] font-medium transition-colors duration-300 ${
                  isActive(link.path)
                    ? 'text-gold'
                    : 'text-ivory/80 hover:text-gold'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA Button - Desktop */}
          <Link
            to="/contact"
            data-testid="nav-cta"
            className="hidden md:block bg-gold text-black px-6 py-3 text-xs uppercase tracking-widest font-medium hover:bg-goldhover transition-colors duration-300"
          >
            Request Introduction
          </Link>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 text-ivory hover:text-gold transition-colors"
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
          >
            {isOpen ? <X size={24} strokeWidth={1.5} /> : <Menu size={24} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden bg-black border-t border-white/10"
          >
            <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                  onClick={() => setIsOpen(false)}
                  className={`block text-sm uppercase tracking-[0.15em] font-medium py-2 transition-colors duration-300 ${
                    isActive(link.path)
                      ? 'text-gold'
                      : 'text-ivory/80 hover:text-gold'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-nav-cta"
                onClick={() => setIsOpen(false)}
                className="block bg-gold text-black px-6 py-4 text-center text-xs uppercase tracking-widest font-medium hover:bg-goldhover transition-colors duration-300 mt-6"
              >
                Request Introduction
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navigation;
